import { useCallback, useEffect, useRef } from 'react';

import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { PayinRequest, PayinResponse, usePayinMutation } from '@/api/payments/payin';
import { TransactionState } from '@/features/paymentProcess/transactionSlice';
import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';

import { PayinState } from './payinSlice';

const timestampNow = Date.now();

const buildPayinPayload = (
  payinState: PayinState,
  transactionState: TransactionState,
  paymentSelectState: PaymentSelectState,
): PayinRequest => ({
  id: paymentSelectState.orderReference,
  transactionId: transactionState.transactionId,
  created: timestampNow,
  updated: timestampNow,
  address1: payinState.address1,
  address2: payinState.address2,
  address3: payinState.address3,
  town: payinState.town,
  country: payinState.country,
  postcode: payinState.postcode,
  Event: null,
  audience: payinState.audience,
  WhatYouDid: payinState.activityOther,
  permission: null,
  permissionEmail: null,
  permissionPhone: null,
  permissionPost: null,
  permissionSMS: null,
  phone: null,
  mobile: null,
  establishmentId: payinState.establishmentId,
  jobTitle: payinState.jobTitle,
  schoolType: payinState.establishmentType,
  establishment_name: payinState.establishmentName,
  organisation: payinState.organisationName,
  organisationFreetext: null,
  fundraising_activity: payinState.activities.join(','),
  ukActiveMember: null,
  certificate: null,
});

export function usePayin() {
  const onSuccess = useRef<((data: PayinResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);

  const payinState = useAppSelector(state => state.payin);
  const transactionState = useAppSelector(state => state.transaction);
  const paymentSelectState = useAppSelector(state => state.paymentSelect);

  const [payinTrigger, {
    isLoading,
    data,
    error,
  }] = usePayinMutation();

  // Trigger payment-service payin submit
  const triggerPayinApi = useCallback(async (
    callbacks?: {
      onSuccess?: (data: PayinResponse) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    onSuccess.current = callbacks?.onSuccess;
    onError.current = callbacks?.onError;

    // Send payment-service payin API call
    const payload = buildPayinPayload(
      payinState,
      transactionState,
      paymentSelectState,
    );
    await payinTrigger(payload);
  }, [
    paymentSelectState,
    payinState,
    transactionState,
    payinTrigger,
  ]);

  useEffect(() => {
    // Wait until payment-service payin is complete
    if (isLoading || (!data && !error)) {
      return;
    }

    // Handle payment-service payin errors
    if (!!error || data?.message !== 'Payin entry has been processed' || !data?.data) {
      logger.error('Error creating payin');
      logger.error(error);

      if (onError.current) {
        onError.current(error);
      }
      return;
    }

    if (onSuccess.current) {
      onSuccess.current(data);
    }
  }, [isLoading, data, error, onSuccess, onError]);

  return {
    triggerPayinApi,
    payinIsLoading: isLoading,
    payinData: data,
    payinError: error,
  };
}
